/**
 * The frames the app's screenshots are shown in: an iPhone for the phone's,
 * a plain window for the desk's. The phone shots are the app's screen without
 * the system's status bar, so the frame draws one (9:41, as Apple's own
 * marketing does) over the top of the screen, and the home indicator under it.
 * They're taken at 390 x 762, a phone's screen less those two strips, so the
 * frame comes out the shape of a real iPhone rather than a tall one.
 */

type Shot = {
  src: string;
  alt: string;
  className?: string;
  /** Eager for the hero; everything below it can wait. */
  eager?: boolean;
};

type WinShot = Shot & {
  /** Named in the title bar, for a window that isn't HireOS's own */
  title?: { name: string; mark: string };
  /** The shot's pixels, where it isn't one of the app's own 2880 x 1800 */
  size?: [number, number];
};

export function Iphone({ src, alt, className = "", eager }: Shot) {
  return (
    <div className={`iphone ${className}`.trim()}>
      <div className="screen">
        <div className="sbar" aria-hidden="true">
          <b>9:41</b>
          <i className="isl" />
          <Status />
        </div>
        <img src={src} alt={alt} width={1170} height={2286} loading={eager ? undefined : "lazy"} />
        <i className="homebar" aria-hidden="true" />
      </div>
    </div>
  );
}

export function Win({ src, alt, className = "", eager, title, size = [2880, 1800] }: WinShot) {
  return (
    <div className={`win ${className}`.trim()}>
      <div className="lights" aria-hidden="true">
        <i />
        <i />
        <i />
        {title && (
          <b className="title">
            <img src={title.mark} alt="" />
            {title.name}
          </b>
        )}
      </div>
      <img src={src} alt={alt} width={size[0]} height={size[1]} loading={eager ? undefined : "lazy"} />
    </div>
  );
}

/** Signal, wifi and battery, as the status bar draws them. */
function Status() {
  return (
    <span className="sys">
      <svg viewBox="0 0 18 12" width="18" height="12" fill="currentColor">
        <rect x="0" y="8" width="3" height="4" rx="1" />
        <rect x="5" y="5.5" width="3" height="6.5" rx="1" />
        <rect x="10" y="3" width="3" height="9" rx="1" />
        <rect x="15" y="0" width="3" height="12" rx="1" />
      </svg>
      <svg viewBox="0 0 16 12" width="16" height="12" fill="currentColor">
        <path d="M8 2.6c2.3 0 4.4.9 6 2.4l1.2-1.3A10.4 10.4 0 0 0 8 .8 10.4 10.4 0 0 0 .8 3.7L2 5a8.6 8.6 0 0 1 6-2.4Z" />
        <path d="M8 6c1.4 0 2.6.5 3.6 1.4l1.2-1.3A7 7 0 0 0 8 4.2a7 7 0 0 0-4.8 1.9l1.2 1.3C5.4 6.5 6.6 6 8 6Z" />
        <path d="M8 9.3c.6 0 1.1.2 1.5.6L8 11.5 6.5 9.9c.4-.4.9-.6 1.5-.6Z" />
      </svg>
      <svg viewBox="0 0 27 12" width="27" height="12">
        <rect x="0.5" y="0.5" width="23" height="11" rx="3.5" fill="none" stroke="currentColor" opacity="0.4" />
        <rect x="2" y="2" width="20" height="8" rx="2" fill="currentColor" />
        <path d="M25 4v4c.8-.3 1.3-1.1 1.3-2S25.8 4.3 25 4Z" fill="currentColor" opacity="0.4" />
      </svg>
    </span>
  );
}
